/**
 * Section "Photos par jalon" de l'onglet Photos (SFD jalonnement
 * Contact/Large, Lot 4 - #122). Regroupe les photos facultatives de chaque
 * jalon (0 a N par moment avant/apres), affichees AU-DESSUS des photos
 * generales de l'intervention (grille avant/apres existante, inchangee) -
 * cf ZonesAgentPageOptimized.tsx.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { zoneService } from '@/services/zoneService';
import { getBackendUrl } from '@/services/api';
import type { DeneignementZone, MomentPhoto } from '@/types';

interface JalonPhotosSectionProps {
  deneignementId: number;
  zones: DeneignementZone[];
  readOnly?: boolean;
}

export function JalonPhotosSection({ deneignementId, zones, readOnly = false }: JalonPhotosSectionProps) {
  const queryClient = useQueryClient();
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: ({ zoneId, moment, file }: { zoneId: number; moment: MomentPhoto; file: File }) =>
      zoneService.uploadPhotoJalon(deneignementId, zoneId, moment, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      setUploadingKey(null);
    },
    onError: () => setUploadingKey(null),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ zoneId, photoId }: { zoneId: number; photoId: number }) =>
      zoneService.deletePhotoJalon(deneignementId, zoneId, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
    },
  });

  const handleSelect = (z: DeneignementZone, moment: MomentPhoto, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `${z.id}-${moment}`;
    setUploadingKey(key);
    uploadMutation.mutate({ zoneId: z.zone.id, moment, file });
    e.target.value = '';
  };

  const renderMoment = (z: DeneignementZone, moment: MomentPhoto, label: string) => {
    const photos = (z.photos || []).filter((p) => p.moment === moment);
    const key = `${z.id}-${moment}`;
    const isUploading = uploadingKey === key && uploadMutation.isPending;

    return (
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-gray-500 block mb-1">{label}</span>
        <div className="flex flex-wrap gap-1.5">
          {photos.map((photo) => (
            <div key={photo.id} className="relative w-14 h-14 flex-shrink-0">
              <img
                src={getBackendUrl(photo.url)}
                alt={label}
                className="w-full h-full object-cover rounded-md border border-gray-200"
              />
              {!readOnly && (
                <button
                  onClick={() => deleteMutation.mutate({ zoneId: z.zone.id, photoId: photo.id })}
                  disabled={deleteMutation.isPending}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center"
                  aria-label="Supprimer la photo"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <label
              className={clsx(
                'w-14 h-14 flex-shrink-0 rounded-md border-2 border-dashed flex items-center justify-center cursor-pointer',
                isUploading ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-primary-400'
              )}
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
              ) : (
                <CameraIcon className="w-5 h-5 text-gray-400" />
              )}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={isUploading}
                onChange={(e) => handleSelect(z, moment, e)}
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2.5">
      {zones.map((z) => (
        <div key={z.id} className="bg-white rounded-xl p-3 flex flex-col gap-2">
          <div className="text-sm font-bold">
            Jalon {z.zone.numero} — {z.zone.libelle ?? z.zone.nom}
          </div>
          <div className="flex gap-3">
            {renderMoment(z, 'avant', 'AVANT')}
            {renderMoment(z, 'apres', 'APRES')}
          </div>
        </div>
      ))}
    </div>
  );
}

export default JalonPhotosSection;
